/**
 * Java semantics the fr3 engine relies on, reproduced exactly so the
 * JavaScript port lays out byte-for-byte like the tablet's Java engine
 * (MyFasManMobile library/services/report/fr3). Only what the engine uses:
 * Character classes, String.trim, Double.parseDouble / Double.toString,
 * String.format("%.Nf") (HALF_UP on the shortest decimal digits), Double.compare,
 * String.compareTo and a SimpleDateFormat subset.
 */

const RE_LETTER = /\p{L}/u;
const RE_DIGIT = /\p{Nd}/u;

/** Character.isLetter(char). */
export const isLetter = (c) => c !== undefined && c !== '' && RE_LETTER.test(c);

/** Character.isDigit(char). */
export const isDigit = (c) => c !== undefined && c !== '' && RE_DIGIT.test(c);

/** Character.isLetterOrDigit(char). */
export const isLetterOrDigit = (c) => isLetter(c) || isDigit(c);

/** Character.digit(c, 16) >= 0 (ASCII hex digits plus Unicode decimal digits). */
export const isHexDigit = (c) => /^[0-9A-Fa-f]$/.test(c) || isDigit(c);

/**
 * Character.isWhitespace(char): Unicode space separators except the
 * non-breaking ones, plus \t \n \u000B \f \r and \u001C-\u001F.
 */
export const isWhitespace = (c) => {
    if (c === undefined || c === '') return false;
    const code = c.charCodeAt(0);
    if (code === 0x09 || code === 0x0A || code === 0x0B || code === 0x0C || code === 0x0D) return true;
    if (code >= 0x1C && code <= 0x1F) return true;
    if (code === 0x00A0 || code === 0x2007 || code === 0x202F) return false;
    return /[\p{Zs}\p{Zl}\p{Zp}]/u.test(c);
};

/** String.trim(): strips code units <= U+0020 at both ends. */
export const javaTrim = (s) => {
    if (s == null) return s;
    let start = 0;
    let end = s.length;
    while (start < end && s.charCodeAt(start) <= 0x20) start++;
    while (end > start && s.charCodeAt(end - 1) <= 0x20) end--;
    return start === 0 && end === s.length ? s : s.substring(start, end);
};

const DEC_RE = /^[+-]?(?:NaN|Infinity|(?:(?:\d+\.?\d*|\.\d+)(?:[eE][+-]?\d+)?[fFdD]?))$/;
const HEX_RE = /^([+-]?)0[xX]([0-9a-fA-F]*)\.?([0-9a-fA-F]*)[pP]([+-]?\d+)[fFdD]?$/;

/** Double.parseDouble: returns null where Java throws NumberFormatException. */
export const parseJavaDouble = (raw) => {
    if (raw == null) return null;
    const s = javaTrim(String(raw));
    if (s === '') return null;
    if (DEC_RE.test(s)) {
        let t = s.replace(/[fFdD]$/, '');
        if (/NaN$/.test(t)) return NaN;
        if (/Infinity$/.test(t)) return t.startsWith('-') ? -Infinity : Infinity;
        return Number(t);
    }
    const hex = HEX_RE.exec(s);
    if (hex && (hex[2] !== '' || hex[3] !== '')) {
        const mant = parseInt((hex[2] + hex[3]) || '0', 16);
        const value = mant * Math.pow(2, Number(hex[4]) - 4 * hex[3].length);
        return hex[1] === '-' ? -value : value;
    }
    return null;
};

/** Long.parseLong(s) (radix 10): integer or null. Values stay exact up to 2^53. */
export const parseJavaLong = (raw) => {
    if (raw == null) return null;
    const s = String(raw);
    if (!/^[+-]?\d+$/.test(s)) return null;
    const v = Number(s);
    if (!Number.isFinite(v)) return null;
    return v;
};

/** (int) cast of a double: truncation, NaN -> 0, saturating. */
export const d2i = (v) => {
    if (Number.isNaN(v)) return 0;
    if (v >= 2147483647) return 2147483647;
    if (v <= -2147483648) return -2147483648;
    const t = Math.trunc(v);
    return t === 0 ? 0 : t;
};

const LONG_MAX = 9223372036854775807;
const LONG_MIN = -9223372036854775808;

/** (long) cast of a double, back as a double: truncation, NaN -> 0, saturating. */
export const d2l = (v) => {
    if (Number.isNaN(v)) return 0;
    if (v >= LONG_MAX) return LONG_MAX;
    if (v <= LONG_MIN) return LONG_MIN;
    const t = Math.trunc(v);
    return t === 0 ? 0 : t;
};

/** Math.round(double) -> long, as a double. */
export const javaRound = (v) => {
    if (Number.isNaN(v)) return 0;
    if (v >= LONG_MAX) return LONG_MAX;
    if (v <= LONG_MIN) return LONG_MIN;
    const r = Math.floor(v + 0.5);
    // floor(x + 0.5) mis-rounds 0.49999999999999994; Java 7+ does not.
    const fixed = (r - v > 0.5) ? r - 1 : r;
    return fixed === 0 ? 0 : fixed;
};

/** Double.compare(a, b). */
export const doubleCompare = (a, b) => {
    if (a < b) return -1;
    if (a > b) return 1;
    const aNaN = Number.isNaN(a);
    const bNaN = Number.isNaN(b);
    if (aNaN || bNaN) {
        if (aNaN && bNaN) return 0;
        return aNaN ? 1 : -1;
    }
    // a == b numerically: -0.0 sorts before 0.0.
    const aNeg = Object.is(a, -0);
    const bNeg = Object.is(b, -0);
    if (aNeg === bNeg) return 0;
    return aNeg ? -1 : 1;
};

/** String.compareTo: difference of the first differing UTF-16 unit, else of the lengths. */
export const stringCompare = (a, b) => {
    const n = Math.min(a.length, b.length);
    for (let i = 0; i < n; i++) {
        const d = a.charCodeAt(i) - b.charCodeAt(i);
        if (d !== 0) return d;
    }
    return a.length - b.length;
};

/** Shortest round-trip decimal digits of |v| (finite, non-zero): { digits, exp } with value = 0.d1d2.. x 10^(exp+1). */
const shortestDigits = (v) => {
    const e = Math.abs(v).toExponential();
    const m = /^(\d)(?:\.(\d+))?e([+-]\d+)$/.exec(e);
    const digits = (m[1] + (m[2] || '')).replace(/0+$/, '') || '0';
    return { digits, exp: parseInt(m[3], 10) };
};

/** Double.toString(d) (Java 19+ shortest representation). */
export const javaDoubleToString = (d) => {
    if (Number.isNaN(d)) return 'NaN';
    if (d === Infinity) return 'Infinity';
    if (d === -Infinity) return '-Infinity';
    if (d === 0) return Object.is(d, -0) ? '-0.0' : '0.0';
    const sign = d < 0 ? '-' : '';
    const { digits, exp } = shortestDigits(d);
    const a = Math.abs(d);
    if (a >= 1e-3 && a < 1e7) {
        let intPart;
        let frac;
        if (exp >= 0) {
            const padded = digits.length <= exp + 1 ? digits + '0'.repeat(exp + 1 - digits.length) : digits;
            intPart = padded.substring(0, exp + 1);
            frac = padded.substring(exp + 1);
        } else {
            intPart = '0';
            frac = '0'.repeat(-exp - 1) + digits;
        }
        if (frac === '') frac = '0';
        return sign + intPart + '.' + frac;
    }
    const mant = digits.substring(0, 1) + '.' + (digits.length > 1 ? digits.substring(1) : '0');
    return sign + mant + 'E' + exp;
};

/**
 * String.format(Locale.ROOT, "%.Nf", v): Java rounds HALF_UP on the
 * shortest decimal digits of the double (not on its exact binary value as
 * Number.toFixed does), e.g. 1.005 -> "1.01".
 */
export const javaFormatFixed = (v, decimals) => {
    if (Number.isNaN(v)) return 'NaN';
    if (v === Infinity) return 'Infinity';
    if (v === -Infinity) return '-Infinity';
    const neg = v < 0 || Object.is(v, -0);
    const zeros = (n) => '0'.repeat(Math.max(0, n));
    let intPart;
    let frac;
    if (v === 0) {
        intPart = '0';
        frac = zeros(decimals);
    } else {
        const { digits, exp } = shortestDigits(v);
        // Digits kept: everything left of the point plus `decimals`.
        const keep = exp + 1 + decimals;
        let kept;
        let pointPos = exp + 1; // digits before the decimal point within `kept`
        if (keep <= 0) {
            const roundUp = keep === 0 && digits.charCodeAt(0) >= 53; // '5'
            if (roundUp) {
                // 10^-decimals
                kept = '1';
                pointPos = 1 - decimals;
            } else {
                kept = '';
            }
        } else if (keep >= digits.length) {
            kept = digits + zeros(keep - digits.length);
        } else {
            const roundUp = digits.charCodeAt(keep) >= 53;
            kept = digits.substring(0, keep);
            if (roundUp) {
                const arr = kept.split('');
                let i = arr.length - 1;
                while (i >= 0) {
                    if (arr[i] === '9') {
                        arr[i] = '0';
                        i--;
                    } else {
                        arr[i] = String.fromCharCode(arr[i].charCodeAt(0) + 1);
                        break;
                    }
                }
                if (i < 0) {
                    arr.unshift('1');
                    pointPos++;
                }
                kept = arr.join('');
            }
        }
        if (kept === '') {
            intPart = '0';
            frac = zeros(decimals);
        } else if (pointPos <= 0) {
            intPart = '0';
            const all = zeros(-pointPos) + kept;
            frac = (all + zeros(decimals)).substring(0, decimals);
        } else {
            const padded = kept.length < pointPos ? kept + zeros(pointPos - kept.length) : kept;
            intPart = padded.substring(0, pointPos).replace(/^0+(?=\d)/, '');
            frac = (padded.substring(pointPos) + zeros(decimals)).substring(0, decimals);
        }
    }
    return (neg ? '-' : '') + intPart + (decimals > 0 ? '.' + frac : '');
};

// -- SimpleDateFormat subset --------------------------------------------------

const pad = (n, width) => {
    const s = String(Math.abs(n));
    return (n < 0 ? '-' : '') + (s.length >= width ? s : '0'.repeat(width - s.length) + s);
};

const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August',
    'September', 'October', 'November', 'December'];
const DAYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
const VALID_LETTERS = 'GyMdkHmsSEDFwWahKzZYuXL';

const isLeap = (y) => (y % 4 === 0 && y % 100 !== 0) || y % 400 === 0;
const daysInMonth = (y, m) => [31, isLeap(y) ? 29 : 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31][m - 1];

/** Fields of a parsed/now date: { y, M (1-12), d, H, m, s, S }. */
export const fieldsOf = (date) => ({
    y: date.getFullYear(),
    M: date.getMonth() + 1,
    d: date.getDate(),
    H: date.getHours(),
    m: date.getMinutes(),
    s: date.getSeconds(),
    S: date.getMilliseconds(),
});

/** Splits a SimpleDateFormat pattern; null when Java would throw IllegalArgumentException. */
const compilePattern = (pattern) => {
    const out = [];
    let i = 0;
    while (i < pattern.length) {
        const c = pattern[i];
        if (c === '\'') {
            if (pattern[i + 1] === '\'') {
                out.push({ lit: '\'' });
                i += 2;
                continue;
            }
            let j = i + 1;
            let text = '';
            let closed = false;
            while (j < pattern.length) {
                if (pattern[j] === '\'') {
                    if (pattern[j + 1] === '\'') {
                        text += '\'';
                        j += 2;
                        continue;
                    }
                    closed = true;
                    break;
                }
                text += pattern[j];
                j++;
            }
            if (!closed) return null;
            out.push({ lit: text });
            i = j + 1;
            continue;
        }
        if (/[a-zA-Z]/.test(c)) {
            if (VALID_LETTERS.indexOf(c) < 0) return null;
            let j = i;
            while (j < pattern.length && pattern[j] === c) j++;
            out.push({ letter: c, count: j - i });
            i = j;
            continue;
        }
        out.push({ lit: c });
        i++;
    }
    return out;
};

const dayOfWeek = (y, M, d) => new Date(Date.UTC(y, M - 1, d)).getUTCDay();
const dayOfYear = (y, M, d) => Math.round((Date.UTC(y, M - 1, d) - Date.UTC(y, 0, 1)) / 86400000) + 1;

/** new SimpleDateFormat(pattern, Locale.ROOT).format(fields); null when the pattern is invalid. */
export const formatDateFields = (pattern, f) => {
    const parts = compilePattern(pattern);
    if (parts === null) return null;
    let out = '';
    for (const p of parts) {
        if (p.lit !== undefined) {
            out += p.lit;
            continue;
        }
        const n = p.count;
        switch (p.letter) {
            case 'G': out += 'AD'; break;
            case 'y':
            case 'Y':
                out += n === 2 ? pad(f.y % 100, 2) : pad(f.y, n);
                break;
            case 'M':
            case 'L':
                if (n >= 4) out += MONTHS[f.M - 1];
                else if (n === 3) out += MONTHS[f.M - 1].substring(0, 3);
                else out += pad(f.M, n);
                break;
            case 'd': out += pad(f.d, n); break;
            case 'D': out += pad(dayOfYear(f.y, f.M, f.d), n); break;
            case 'E': {
                const name = DAYS[dayOfWeek(f.y, f.M, f.d)];
                out += n >= 4 ? name : name.substring(0, 3);
                break;
            }
            case 'u': {
                const dow = dayOfWeek(f.y, f.M, f.d);
                out += pad(dow === 0 ? 7 : dow, n);
                break;
            }
            case 'F': out += pad(Math.floor((f.d - 1) / 7) + 1, n); break;
            case 'a': out += f.H < 12 ? 'AM' : 'PM'; break;
            case 'H': out += pad(f.H, n); break;
            case 'k': out += pad(f.H === 0 ? 24 : f.H, n); break;
            case 'K': out += pad(f.H % 12, n); break;
            case 'h': out += pad(f.H % 12 === 0 ? 12 : f.H % 12, n); break;
            case 'm': out += pad(f.m, n); break;
            case 's': out += pad(f.s, n); break;
            case 'S': out += pad(f.S || 0, n); break;
            default:
                // w W z Z X: not used by the templates; Java would print them.
                out += '';
                break;
        }
    }
    return out;
};

/**
 * Non-lenient SimpleDateFormat.parse for the numeric patterns the engine
 * uses (y M d H m s plus literals). Like Java, trailing text after the
 * pattern is ignored and blanks before a number are skipped. Returns the
 * fields, or null where Java throws ParseException.
 */
export const parseDateFields = (pattern, text) => {
    const parts = compilePattern(pattern);
    if (parts === null) return null;
    const f = { y: 1970, M: 1, d: 1, H: 0, m: 0, s: 0, S: 0 };
    let pos = 0;
    for (const p of parts) {
        if (p.lit !== undefined) {
            for (let k = 0; k < p.lit.length; k++) {
                if (pos >= text.length || text[pos] !== p.lit[k]) return null;
                pos++;
            }
            continue;
        }
        while (pos < text.length && (text[pos] === ' ' || text[pos] === '\t')) pos++;
        let j = pos;
        let sign = 1;
        if (text[j] === '-') {
            sign = -1;
            j++;
        }
        const digitStart = j;
        while (j < text.length && text[j] >= '0' && text[j] <= '9') j++;
        if (j === digitStart) return null;
        const value = sign * parseInt(text.substring(digitStart, j), 10);
        pos = j;
        switch (p.letter) {
            case 'y':
            case 'Y': f.y = value; break;
            case 'M': f.M = value; break;
            case 'd': f.d = value; break;
            case 'H': f.H = value; break;
            case 'm': f.m = value; break;
            case 's': f.s = value; break;
            default: return null;
        }
    }
    // Non-lenient calendar validation.
    if (f.y < 1 || f.M < 1 || f.M > 12 || f.d < 1 || f.d > daysInMonth(f.y, f.M)) return null;
    if (f.H < 0 || f.H > 23 || f.m < 0 || f.m > 59 || f.s < 0 || f.s > 59) return null;
    return f;
};
